import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2, User, UserPlus, Trash2 } from 'lucide-react';
import { DeleteUserInvite } from '@/components/DeleteUserInvite';
import { Button } from '@/components/ui/button';
import { PageTopNav } from '@/components/nav/PageTopNav';
import { PageSectionTitle } from '@/components/nav/PageSectionTitle';
import { useOrgContext } from '@/contexts/OrgContext';
import { useNavigate } from 'react-router';
import { OrgAdminEditRole } from '@/components/org/OrgAdminEditRole';

// Define the user type based on API response
interface OrgUser {
  orgUserId: string;
  orgUserRole: "USER" | "ADMIN";
  userId: string;
  userRole: "USER" | "ADMIN";
  email: string;
  name: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
}

// Define the requested user type
interface RequestedUser {
  id: string;
  email: string;
  orgId: string;
  role: string;
  createdAt: string;
  createdBy?: string;
  updatedAt: string;
}

const UsersOrgAdmin: React.FC = () => {
  const navigate = useNavigate();
  const { organisation } = useOrgContext();

  // Fetch users from the organization
  const { data: users, isLoading, error } = useQuery({
    queryKey: ['organizationUsers', organisation.id],
    queryFn: async () => {
      const response = await fetch(`/api/org/${organisation.id}/users`);
      if (!response.ok) {
        throw new Error('Failed to fetch organization users');
      }
      return response.json() as Promise<OrgUser[]>;
    },
  });

  // Fetch requested users from the organization
  const {
    data: invitedUsers,
    isLoading: isLoadingRequested,
    error: errorRequested
  } = useQuery({
    queryKey: ['invitedUsers', organisation.id],
    queryFn: async () => {
      const response = await fetch(`/api/org/${organisation.id}/invitedUsers`);
      if (!response.ok) {
        throw new Error('Failed to fetch invited users');
      }
      return response.json() as Promise<RequestedUser[]>;
    },
  });

  return (
    <div className="container mx-auto ">
      <PageTopNav pageName="Organization Users" rightItem={<Button
        onClick={() => navigate(`/app/org/${organisation.id}/users/invite`)}
        className=""
      >
        <UserPlus className=" h-4 w-4" />
        <span className="hidden sm:inline">Invite User</span>
      </Button>} />

      <div className="">
        <PageSectionTitle sectionName="All Active Users" description="Users in your current organization." />
        <div className="p-4">
          {isLoading ? (
            <div className="flex justify-center p-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : error ? (
            <div className="bg-red-50 p-4 rounded-md text-red-800">
              Failed to load users. Please try again.
            </div>
          ) : (
            <div className="space-y-4">
              {users && users.length > 0 ? (
                <>
                  {/* Desktop/Table view */}
                  <div className="hidden sm:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Email</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Joined</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {users.map((user) => (
                          <TableRow key={user.orgUserId}>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{user.name ? `${user.name}`.trim() : '-'}</TableCell>
                            <TableCell>
                              <OrgAdminEditRole orgUserId={user.orgUserId} initRole={user.orgUserRole} />
                            </TableCell>
                            <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {/* Mobile/Card view */}
                  <div className="block sm:hidden space-y-4">
                    {users.map((user) => (
                      <Card key={user.orgUserId} className="w-full">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <User className="w-5 h-5 text-muted-foreground" />
                            {user.name ? `${user.name}`.trim() : user.email}
                          </CardTitle>
                          <CardDescription>{user.email}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-1">
                          <div>
                            <OrgAdminEditRole orgUserId={user.orgUserId} initRole={user.orgUserRole} />
                            <span className="font-semibold">Role:</span> <span className="capitalize">{user.orgUserRole}</span>
                          </div>
                          <div>
                            <span className="font-semibold">Joined:</span> {new Date(user.createdAt).toLocaleDateString()}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center p-4 bg-muted/50 rounded-md">
                  <User className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-muted-foreground">No users found in this organization.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="">
        <PageSectionTitle
          sectionName="Invited Users"
          description="Users who have been invited to join your organization." />

        <div className="p-4">
          {isLoadingRequested ? (
            <div className="flex justify-center p-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : errorRequested ? (
            <div className="bg-red-50 p-4 rounded-md text-red-800">
              Failed to load requested users. Please try again.
            </div>
          ) : (
            <div className="space-y-4">
              {invitedUsers && invitedUsers.length > 0 ? (
                <>
                  {/* Desktop/Table view */}
                  <div className="hidden sm:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Email</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Invitation Date</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {invitedUsers.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>{user.email}</TableCell>
                            <TableCell className="capitalize">{user.role}</TableCell>
                            <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                            <TableCell>
                              <DeleteUserInvite orgId={organisation.id} reqUserId={user.id} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {/* Mobile/Card view */}
                  <div className="block sm:hidden space-y-4">
                    {invitedUsers.map((user) => (
                      <Card key={user.id} className="w-full">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-muted-foreground" />
                            {user.email}
                            <DeleteUserInvite orgId={organisation.id} reqUserId={user.id} buttonProps={{ className: 'ml-auto' }} />
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-1">
                          <div>
                            <span className="font-semibold">Role:</span> <span className="capitalize">{user.role}</span>
                          </div>
                          <div>
                            <span className="font-semibold">Invited:</span> {new Date(user.createdAt).toLocaleDateString()}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center p-4 bg-muted/50 rounded-md">
                  <UserPlus className="size-6 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-muted-foreground">No invited users at this time.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsersOrgAdmin;
